import { Box, Typography, alpha } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ExtractedRecord, type DocumentTransformation } from "@/types";
import type { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import type { CriticalFieldAdvisoryContext } from "../../hooks/useCriticalFieldAdvisory";
import type { MappingValidationError } from "../../utils/fieldMappingValidation";
import { usePdfSourcePreview } from "../../hooks/usePdfSourcePreview";
import { PdfViewerWithHighlights, type PdfHighlight, type PdfHighlightStatus } from "@/components/common/PdfViewer";
import { TargetFieldSelect, type UnifiedTarget } from "./TargetFieldSelect";
import { MappingBlockingAlert } from "./MappingBlockingAlert";
import { useMappingBlockers, rankSourcesByFieldMatch, type BlockingItem } from "./mappingBlockers";
import { useMappingOptions, useClaimedKeys, suggestedFieldForSource, currentTargetsForHeader } from "./mappingOptions";
import { coordinatesToInches } from "../../utils/pdfHighlights";

interface PdfMappingSurfaceProps {
  transformation: DocumentTransformation;
  /** Records to preview (#6755) — the transformation no longer carries them. */
  records?: ExtractedRecord[];
  documentId?: string;
  /** Detected field labels (PDF key anchors), in document order. */
  detectedHeaders: string[];
  editedMappingsBySchema: Record<string, Record<string, string[]>>;
  fieldsBySchema: Record<string, SchemaFieldDefinition[]>;
  allSchemaKeys: string[];
  onChange: (header: string, targets: { schema: string; field: string }[]) => void;
  validationErrors?: Record<string, MappingValidationError[]>;
  advisoryContext?: CriticalFieldAdvisoryContext;
}

/**
 * PDF map-on-document remap surface (#6119). Vertical split: the map-to list on
 * the left (one row per detected label → the shared `TargetFieldSelect`, with
 * the shared blocking alert on top), the rendered PDF on the right. Selecting a
 * row focuses the PDF on that field's page and highlights its region (where the
 * extractor captured coordinates). Label-anchored — reuses the same key-based
 * mapping model as the spreadsheet grid; clickable regions / region→row sync are
 * a follow-up (#6120).
 */
export const PdfMappingSurface = ({
  transformation,
  records,
  documentId,
  detectedHeaders,
  editedMappingsBySchema,
  fieldsBySchema,
  allSchemaKeys,
  onChange,
  validationErrors,
  advisoryContext = {},
}: PdfMappingSurfaceProps) => {
  const { options, optionByKey, criticalByKey } = useMappingOptions(fieldsBySchema, allSchemaKeys);
  const claimedKeys = useClaimedKeys(editedMappingsBySchema, allSchemaKeys);

  // Per-label extracted value + page + region highlight, from the extraction.
  const labelInfo = useMemo(() => {
    const info = new Map<string, { value: string; page: number | null; highlight: PdfHighlight | null }>();
    for (const rec of records ?? []) {
      for (const f of rec.extracted_fields ?? []) {
        const key = f.source?.key;
        if (!key) continue;
        const existing = info.get(key);
        const value = f.extracted_value === null || f.extracted_value === undefined ? "" : String(f.extracted_value);
        const hasCoords = Boolean(f.source?.coordinates?.length);
        const page = f.source?.page_number ?? null;
        // A region highlight needs BOTH coordinates and a known page. page_number
        // and coordinates are independent backend metadata keys (transformer.go),
        // so an absent page_number means the page is unknown — skip the highlight
        // rather than assume page 1 (which would mis-place a page-2+ field onto
        // page 1 on a multi-page doc). PR 4365 review.
        const highlight: PdfHighlight | null =
          hasCoords && page
            ? {
                fieldName: key,
                value,
                metadata: {
                  pageNumber: page,
                  boundingBox: coordinatesToInches(f.source!.coordinates!),
                  textSpan: value,
                },
              }
            : null;
        // Keep the first non-empty value / first page / first highlight seen.
        info.set(key, {
          value: existing?.value || value,
          page: existing?.page ?? page,
          highlight: existing?.highlight ?? highlight,
        });
      }
    }
    return info;
    // Depends on `records`, NOT `transformation` (#6856): records arrive on an
    // async fetch after the dialog opens, so a transformation-keyed memo
    // computed once over [] and never saw them. See HorizontalMappingGrid's
    // rows memo for the full history.
  }, [records]);

  const columns = useMemo(() => {
    if (detectedHeaders.length > 0) return detectedHeaders;
    return Array.from(labelInfo.keys());
  }, [detectedHeaders, labelInfo]);

  // PDF bytes (lazy-fetched; partner-scoped auth handled by the hook).
  const { pdfData, isLoading, error } = usePdfSourcePreview({
    documentId,
    transformation,
    enabled: true,
  });

  // Selection drives the PDF focus + highlight and is the alert's jump target.
  const [selected, setSelected] = useState<number | null>(null);
  const rowRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [flashRow, setFlashRow] = useState<number | null>(null);
  useEffect(() => {
    if (flashRow === null) return;
    const t = setTimeout(() => setFlashRow(null), 1600);
    return () => clearTimeout(t);
  }, [flashRow]);

  const selectLabel = (i: number) => {
    if (i < 0 || i >= columns.length) return;
    setSelected(i);
    setFlashRow(i);
    rowRefs.current.get(i)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const selectedLabel = selected !== null ? columns[selected] : null;
  const selectedInfo = selectedLabel ? labelInfo.get(selectedLabel) : undefined;
  // Prefer the selected field's highlight page so the page-focus and the region
  // scroll (scrollToHighlight) never target different pages; fall back to the
  // label's page when it has no coordinate highlight.
  const focusPage = selectedInfo?.highlight?.metadata.pageNumber ?? selectedInfo?.page ?? null;

  // Labels flagged by the shared validator (duplicate / mutually-exclusive) →
  // their region shows red, matching the spreadsheet legend.
  const erroredLabels = useMemo(() => {
    const s = new Set<string>();
    for (const errs of Object.values(validationErrors ?? {})) {
      for (const e of errs) for (const h of e.sourceHeaders ?? []) s.add(h);
    }
    return s;
  }, [validationErrors]);

  // Every label with a coordinate region renders a polygon — color-coded by
  // mapping state (mapped → extracted/green, unmapped → grey, validation error →
  // red), the selected one emphasized. Click any region to select its row.
  const highlights = useMemo<PdfHighlight[]>(() => {
    const out: PdfHighlight[] = [];
    columns.forEach((label, i) => {
      const h = labelInfo.get(label)?.highlight;
      if (!h) return;
      const mapped = allSchemaKeys.some((s) => (editedMappingsBySchema[s]?.[label]?.length ?? 0) > 0);
      const status: PdfHighlightStatus = erroredLabels.has(label) ? "error" : mapped ? "extracted" : "unmapped";
      out.push({ ...h, status, emphasized: selected === i });
    });
    return out;
  }, [columns, labelInfo, erroredLabels, editedMappingsBySchema, allSchemaKeys, selected]);

  const mappedCount = useMemo(
    () => columns.filter((l) => allSchemaKeys.some((s) => (editedMappingsBySchema[s]?.[l]?.length ?? 0) > 0)).length,
    [columns, allSchemaKeys, editedMappingsBySchema]
  );

  // Blocking issues (shared) → resolve to label-row indices for jump.
  const blockers = useMappingBlockers({
    allSchemaKeys,
    editedMappingsBySchema,
    validationErrors,
    advisoryContext,
    optionByKey,
  });
  const colIndexByLabel = useMemo(() => {
    const m = new Map<string, number>();
    columns.forEach((c, i) => {
      if (!m.has(c)) m.set(c, i);
    });
    return m;
  }, [columns]);
  const resolveTargets = (item: BlockingItem): number[] => {
    // Duplicate and unknown-field carry the exact offending source labels — jump
    // to those precisely; only missing-required token-matches. (#6425)
    if (item.kind === "duplicate" || item.kind === "unknown-field") {
      return (item.sourceHeaders ?? []).map((h) => colIndexByLabel.get(h) ?? -1).filter((i) => i >= 0);
    }
    const opt = optionByKey.get(item.key);
    return rankSourcesByFieldMatch(opt?.label ?? item.label, opt?.field ?? "", columns);
  };

  const currentTargetsFor = (label: string): UnifiedTarget[] =>
    currentTargetsForHeader(label, allSchemaKeys, editedMappingsBySchema, optionByKey);

  if (columns.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No detected fields in this document.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 2, height: "68vh", minHeight: 440 }}>
      {/* ── Left: the work — progress/legend + blocking alert + map-to list ── */}
      <Box sx={{ flex: "0 0 44%", minWidth: 320, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* Progress + color key — gives the header strip a job and explains the
            highlight colors on the document. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {mappedCount} of {columns.length} field{columns.length !== 1 ? "s" : ""} mapped
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {[
              ["success.main", "Mapped"] as const,
              ["text.disabled", "Unmapped"] as const,
              ...(erroredLabels.size > 0 ? [["error.main", "Issue"] as const] : []),
            ].map(([color, label]) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <MappingBlockingAlert
          items={blockers}
          resolveTargets={resolveTargets}
          onLocate={selectLabel}
          sourceUnit="field"
        />
        <Box sx={{ overflowY: "auto", pr: 0.5, minHeight: 0 }}>
          {columns.map((label, i) => {
            const value = currentTargetsFor(label);
            const isMapped = value.length > 0;
            const sample = labelInfo.get(label)?.value ?? "";
            const isSelected = selected === i;
            const isError = erroredLabels.has(label);
            return (
              <Box
                key={`${label}-${i}`}
                ref={(el: HTMLDivElement | null) => {
                  rowRefs.current.set(i, el);
                }}
                onClick={() => setSelected(i)}
                sx={(theme) => {
                  // The left rail mirrors the PDF highlight legend so the list and
                  // the document speak one color language: mapped → green,
                  // unmapped → grey, validation issue → red.
                  const rail = isError
                    ? theme.palette.error.main
                    : isMapped
                      ? theme.palette.success.main
                      : theme.palette.text.disabled;
                  const dark = theme.palette.mode === "dark";
                  return {
                    px: 1.25,
                    py: 1,
                    mb: 1,
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: isSelected ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider,
                    borderLeft: `3px solid ${rail}`,
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, dark ? 0.16 : 0.06)
                      : theme.palette.background.paper,
                    boxShadow:
                      flashRow === i ? `0 0 0 2px ${theme.palette.error.main}` : isSelected ? theme.shadows[1] : "none",
                    transition: "box-shadow 150ms, background-color 150ms, border-color 150ms",
                    cursor: "pointer",
                    scrollMarginBlock: "8px",
                    // Subtle lift on hover instead of a background swap, so it
                    // doesn't fight the selected tint.
                    "&:hover": {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      boxShadow: theme.shadows[1],
                    },
                  };
                }}
              >
                {/* Label + value on the left, the map-to dropdown on the right,
                    so each row stays compact (Dillon's review). */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.4 }}>
                      {label}
                    </Typography>
                    <Box
                      component="span"
                      title={sample || undefined}
                      sx={(theme) => ({
                        display: "inline-block",
                        maxWidth: "100%",
                        mt: 0.25,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 0.75,
                        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.045),
                        fontFamily: "monospace",
                        fontSize: "0.72rem",
                        lineHeight: 1.6,
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "bottom",
                      })}
                    >
                      {sample || "—"}
                    </Box>
                  </Box>
                  {/* Stop row-select from firing while interacting with the dropdown.
                      Focusing the field (keyboard tab) still selects the row, so
                      keyboard users get the PDF highlight without a mouse click. */}
                  <Box
                    sx={{ flexShrink: 0, width: 200 }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setSelected(i)}
                  >
                    <TargetFieldSelect
                      options={options}
                      criticalByKey={criticalByKey}
                      value={value}
                      onChange={(targets) => onChange(label, targets)}
                      suggestedKey={suggestedFieldForSource(label, options)}
                      claimedKeys={claimedKeys}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* ── Right: the document ── */}
      {/* overflow:auto (not hidden) is load-bearing: PdfViewer renders its pages
          at natural height with flexShrink:0 and delegates vertical scrolling to
          the nearest ancestor with overflow:auto. Clipping here left the page
          uncscrollable. */}
      <Box
        sx={(theme) => ({
          flex: 1,
          minWidth: 0,
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          overflow: "auto",
          // Faint backdrop so the white PDF pages read as sitting on a surface,
          // like a real document viewer, rather than in a bare bordered box.
          bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.03) : theme.palette.grey[50],
        })}
      >
        <PdfViewerWithHighlights
          source={pdfData}
          isLoading={isLoading}
          error={error}
          focusPage={focusPage}
          highlights={highlights}
          scrollToHighlight
          onHighlightClick={(fieldName) => {
            const idx = colIndexByLabel.get(fieldName);
            if (idx !== undefined) selectLabel(idx);
          }}
        />
      </Box>
    </Box>
  );
};
